import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import ValueProposition from './components/ValueProposition';

const LoginPage = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('techtribes_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('techtribes_user', JSON.stringify(userData));
  };

  return (
    <>
      <Helmet>
        <title>Login - TechTribes Commerce | Secure Access to ICT Solutions</title>
        <meta name="description" content="Sign in to your TechTribes account to access comprehensive ICT solutions, manage RFQs, and track orders. Secure authentication for customers, admins, and partners." />
        <meta name="keywords" content="TechTribes login, ICT solutions Uganda, secure authentication, business technology, RFQ management" />
        <meta property="og:title" content="Login - TechTribes Commerce" />
        <meta property="og:description" content="Access your TechTribes account for ICT solutions, quotations, and order management." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        {/* Header */}
        <div className="bg-card border-b border-border shadow-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                  <span className="text-white font-bold text-lg">T</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">TechTribes Commerce</h1>
                  <p className="text-xs text-muted-foreground">Innovate, Connect, Transform</p>
                </div>
              </div>
              
              <div className="hidden sm:flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span>Secure Login</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Login Form - Center Column */}
            <div className="lg:col-start-2 lg:col-span-1">
              <LoginForm onLogin={handleLogin} />
            </div>

            {/* Trust Signals - Left Column (Desktop) / Below Form (Mobile) */}
            <div className="lg:col-start-1 lg:col-span-1 lg:order-first order-last">
              <div className="sticky top-8">
                <TrustSignals />
              </div>
            </div>

            {/* Value Proposition - Right Column (Desktop) / Below Trust Signals (Mobile) */}
            <div className="lg:col-start-3 lg:col-span-1">
              <div className="sticky top-8">
                <ValueProposition />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">TechTribes Creations Ltd</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Leading ICT solutions provider in Uganda, serving government agencies, SMEs, and enterprises.
                </p>
                <p className="text-xs text-muted-foreground">
                  Licensed by Uganda Communications Commission
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Contact Information</h3>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>📍 Kampala, Uganda</p>
                  <p>📞 +256 700 123 456</p>
                  <p>✉️ info@techtribes.ug</p>
                  <p>🌐 www.techtribes.ug</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Quick Links</h3>
                <div className="space-y-2 text-xs">
                  <a href="/product-catalog" className="block text-muted-foreground hover:text-primary transition-smooth">
                    Product Catalog
                  </a>
                  <a href="/rfq-request-form" className="block text-muted-foreground hover:text-primary transition-smooth">
                    Request Quote
                  </a>
                  <a href="/register" className="block text-muted-foreground hover:text-primary transition-smooth">
                    Create Account
                  </a>
                </div>
              </div>
            </div>
            
            <div className="border-t border-border mt-8 pt-6 text-center">
              <p className="text-xs text-muted-foreground">
                © {new Date()?.getFullYear()} TechTribes Creations Limited. All rights reserved. | 
                <span className="ml-1">Privacy Policy</span> | 
                <span className="ml-1">Terms of Service</span>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LoginPage;